<template>
  <SignupStepFrame
    :step-number="5"
    :step-total="5"
    eyebrow="Step 5 — Ready to launch"
    :title="titleHtml"
    subtitle="One click and your workspace is live. Here's what your team can expect."
    :is-last-step="true"
    :can-go-next="!submitting"
    :loading="submitting"
    @next="onSubmit"
    @back="onBack"
    @saveAndExit="onSaveAndExit"
  >
    <div v-if="submitError" class="launch__error" role="alert">
      <strong>We couldn't create your workspace.</strong>
      <span>{{ submitError }}</span>
    </div>

    <!-- 1. Workspace summary card -->
    <section class="workspace-card" aria-label="Workspace summary">
      <header class="workspace-card__head">
        <div class="workspace-card__identity">
          <div class="workspace-card__eyebrow">Workspace</div>
          <h2 class="workspace-card__name">{{ companyName }}</h2>
        </div>
        <a
          class="workspace-card__edit"
          href="#"
          @click.prevent="editModalVisible = true"
        >
          <span>Edit details</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2 10h2.5l5.2-5.2-2.5-2.5L2 7.5V10z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </header>

      <div v-if="hasInvitees" class="workspace-card__roster">
        <div class="workspace-card__avatars" :aria-label="rosterAriaLabel">
          <span
            v-for="(p, i) in avatarPeople"
            :key="i"
            class="workspace-avatar"
            :class="avatarClass(p)"
            :title="avatarTitle(p)"
          >{{ avatarInitials(p) }}</span>
          <span
            v-if="extraAvatarCount > 0"
            class="workspace-avatar workspace-avatar--more"
            :title="`+${extraAvatarCount} more`"
          >+{{ extraAvatarCount }}</span>
        </div>
        <p class="workspace-card__roster-line">
          <strong>{{ teammatesCount }}</strong>
          {{ teammatesCount === 1 ? 'teammate' : 'teammates' }} joining you on launch.
        </p>
      </div>
      <div v-else class="workspace-card__roster workspace-card__roster--solo">
        <span class="workspace-avatar workspace-avatar--solo">{{ signerInitials }}</span>
        <p class="workspace-card__roster-line">
          You're starting solo — invite your team anytime from <strong>Settings → People</strong>.
        </p>
      </div>

      <dl class="workspace-stats">
        <div class="workspace-stat">
          <dt>People</dt>
          <dd>{{ peopleCount }}</dd>
        </div>
        <div class="workspace-stat">
          <dt>Teams</dt>
          <dd>{{ teamCount || '—' }}</dd>
        </div>
        <div class="workspace-stat">
          <dt>Cadence</dt>
          <dd>{{ cadenceLabel }}</dd>
        </div>
        <div class="workspace-stat">
          <dt>Rewards</dt>
          <dd>{{ rewardCount }}</dd>
        </div>
      </dl>
    </section>

    <!-- 2. Numbered launch timeline -->
    <ol class="launch-timeline signup-stagger" aria-label="Launch timeline">
      <li class="launch-step">
        <div class="launch-step__rail" aria-hidden="true">
          <span class="launch-step__icon">
            <svg width="18" height="18" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7.5l8 5 4.5-2.8M3 7.5v8.5a2 2 0 002 2h7M3 7.5a2 2 0 012-2h12a2 2 0 012 2v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 17l2.2 2.2L20.5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
        <div class="launch-step__body">
          <div class="launch-step__index">01</div>
          <div class="launch-step__title">Invitations delivered</div>
          <p class="launch-step__desc">{{ inviteCardCopy }}</p>
        </div>
      </li>

      <li class="launch-step">
        <div class="launch-step__rail" aria-hidden="true">
          <span class="launch-step__icon">
            <svg width="18" height="18" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 11h3.5L7 6l3.5 10L12.5 9l1.5 4h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
        <div class="launch-step__body">
          <div class="launch-step__index">02</div>
          <div class="launch-step__title">A pulse on your team</div>
          <p class="launch-step__desc">
            Your first questions go out at {{ deliveryTimeLabel }} tomorrow. Within a week you'll see how engaged, focused, and connected your team really is.
          </p>
        </div>
      </li>

      <li class="launch-step">
        <div class="launch-step__rail" aria-hidden="true">
          <span class="launch-step__icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 16l5-5 4 4 7-8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 7h5v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
        <div class="launch-step__body">
          <div class="launch-step__index">03</div>
          <div class="launch-step__title">1% better together, every day</div>
          <p class="launch-step__desc">
            Recognition compounds. Small moments of acknowledgment, repeated daily, shape the culture you want to build.
          </p>
        </div>
      </li>
    </ol>

    <!-- 3. Tiny disclaimer -->
    <p class="launch-disclaimer">
      By continuing, you agree to our <a href="https://happily.ai/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="https://happily.ai/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
    </p>

    <!-- 4. Custom footer with finale CTA -->
    <template #footer>
      <footer class="launch-footer">
        <button
          type="button"
          class="launch-footer__back"
          :disabled="submitting"
          @click="onBack"
        >
          Back
        </button>
        <button
          type="button"
          class="signup-btn signup-btn--finale launch-footer__cta"
          :disabled="submitting"
          @click="onSubmit"
        >
          <span v-if="submitting" class="launch-footer__spinner" aria-hidden="true"></span>
          <span class="launch-footer__cta-label">{{ submitting ? 'Creating…' : 'Create account & invite team' }}</span>
          <svg
            v-if="!submitting"
            class="launch-footer__cta-arrow"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M3.5 8h9M9 4.5l3.5 3.5L9 11.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </footer>
    </template>

    <!-- 5. Edit details modal -->
    <LaunchEditModal
      :visible="editModalVisible"
      :wizard-data="wizardData"
      @close="editModalVisible = false"
      @goto-step="onGotoStep"
    />

  </SignupStepFrame>
</template>

<script>
/**
 * WizardReviewStep — Step 5 of the self-serve signup wizard. The
 * forward-looking launch screen.
 *
 * Sections:
 *   1. Workspace summary card — Instrument-Serif company name, invitee
 *      avatar stack, 4 labeled stat tiles, "Edit details" link.
 *   2. Numbered launch timeline — three cards (01/02/03) connected by a
 *      hairline gradient thread in the gutter. The third card adapts to
 *      reward-credit state (default / credits-now / credits-later).
 *   3. Tiny T&C disclaimer.
 *   4. Custom footer with the finale gradient CTA (arrow that nudges on
 *      hover, spinner during submit).
 *   5. <LaunchEditModal> for granular review on demand.
 */
import SignupStepFrame from './SignupStepFrame.vue'
import LaunchEditModal from './_primitives/LaunchEditModal.vue'
import { mapGetters, mapState } from 'vuex'

const CADENCE_LABELS = {
  light: 'Light',
  balanced: 'Balanced',
  intense: 'Intense',
}

const AVATAR_BUCKETS = ['c1', 'c2', 'c3', 'c4', 'c5']
const MAX_VISIBLE_AVATARS = 5

function colorForTeam(team) {
  const s = String(team || '').trim().toLowerCase()
  if (!s) return 'c0'
  let h = 0
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return AVATAR_BUCKETS[h % AVATAR_BUCKETS.length]
}

function formatDeliveryTime(value) {
  if (!value || typeof value !== 'string') return ''
  const m = value.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return ''
  let h = parseInt(m[1], 10)
  const mins = m[2]
  if (Number.isNaN(h)) return ''
  const period = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${mins} ${period}`
}

function initialsFor(person) {
  if (!person) return '?'
  const source = String(person.name || person.email || '').trim()
  if (!source) return '?'
  if (person.name) {
    const parts = source.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return parts[0].slice(0, 2).toUpperCase()
  }
  const local = source.split('@')[0] || source
  return local.slice(0, 2).toUpperCase()
}

export default {
  name: 'WizardReviewStep',
  components: { SignupStepFrame, LaunchEditModal },
  data() {
    return {
      editModalVisible: false,
      titleHtml: 'Here\'s what <b>happens next</b>.',
    }
  },
  computed: {
    ...mapGetters('signup', ['wizardData', 'selectedRewardIds']),
    ...mapState('signup', {
      submitting: (s) => !!s.submitting,
      submitError: (s) => s.submitError || '',
    }),
    company() {
      return (this.wizardData && this.wizardData.company) || {}
    },
    people() {
      const p = this.wizardData && this.wizardData.people
      return Array.isArray(p) ? p : []
    },
    signer() {
      return this.people.find((p) => p && p.you) || this.people[0] || null
    },
    teammates() {
      // Everyone except the signer.
      return this.people.filter((p) => p && !p.you)
    },
    companyName() {
      return (this.company && this.company.name) || 'Your workspace'
    },
    hasInvitees() {
      return this.teammates.length > 0
    },
    avatarPeople() {
      return this.teammates.slice(0, MAX_VISIBLE_AVATARS)
    },
    extraAvatarCount() {
      return Math.max(0, this.teammates.length - MAX_VISIBLE_AVATARS)
    },
    rosterAriaLabel() {
      const n = this.teammates.length
      return `${n} ${n === 1 ? 'teammate' : 'teammates'} joining`
    },
    signerInitials() {
      return initialsFor(this.signer || {})
    },
    peopleCount() {
      return this.people.length
    },
    teammatesCount() {
      return this.teammates.length
    },
    teamCount() {
      const set = new Set()
      this.people.forEach((p) => {
        const t = p && p.team
        if (t) set.add(t)
      })
      return set.size
    },
    cadenceLabel() {
      const c = String((this.company && this.company.cadence) || 'balanced').toLowerCase()
      return CADENCE_LABELS[c] || 'Balanced'
    },
    rewardCount() {
      return Array.isArray(this.selectedRewardIds) ? this.selectedRewardIds.length : 0
    },
    deliveryTimeLabel() {
      const raw = (this.company && this.company.delivery_time) || '08:00'
      return formatDeliveryTime(raw) || '8:00 AM'
    },
    inviteCardCopy() {
      const n = this.teammatesCount
      const who = n > 0 ? `${n} team member${n === 1 ? '' : 's'}` : 'your team'
      const company = this.companyName
      return `We'll email ${who} a welcome to ${company}. They can join immediately and start recognizing each other today.`
    },
  },
  methods: {
    avatarClass(person) {
      return colorForTeam(person && person.team)
    },
    avatarInitials(person) {
      return initialsFor(person)
    },
    avatarTitle(person) {
      if (!person) return ''
      return person.name || person.email || ''
    },
    onSubmit() {
      if (this.submitting) return
      this.$emit('next')
    },
    onBack() {
      if (this.submitting) return
      this.$emit('back')
    },
    onSaveAndExit() {
      this.$emit('saveAndExit')
    },
    onGotoStep(stepNumber) {
      this.$emit('goto-step', stepNumber)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.launch__error {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(204, 48, 48, 0.06);
  border: 1px solid rgba(204, 48, 48, 0.25);
  margin-bottom: 16px;

  strong {
    font-family: 'Lato', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 800;
    color: var(--signup-danger);
  }
  span {
    font-family: 'Lato', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--signup-text);
  }
}

/* ── 1. Workspace summary ────────────────────────────────────── */
.workspace-card {
  position: relative;
  padding: 16px 20px 14px;
  background:
    linear-gradient(180deg, rgba(247, 185, 36, 0.05) 0%, rgba(108, 48, 204, 0.025) 70%, transparent 100%),
    var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 14px;
  box-shadow: var(--signup-shadow-lift);
  margin-bottom: 14px;
  overflow: hidden;

  /* Subtle gold horizon arc in the top-right corner — restrained
     ornament that nods to the launch / liftoff metaphor. */
  &::after {
    content: '';
    position: absolute;
    top: -36px;
    right: -36px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(247, 185, 36, 0.18) 0%, transparent 70%);
    pointer-events: none;
  }
}

.workspace-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
}

.workspace-card__identity {
  min-width: 0;
}
.workspace-card__eyebrow {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--signup-text-muted);
  margin-bottom: 2px;
}
.workspace-card__name {
  margin: 0;
  font-family: 'Instrument Serif', Georgia, serif;
  font-style: italic;
  font-size: 24px;
  line-height: 1.1;
  color: var(--signup-text);
  letter-spacing: -0.005em;
  word-break: break-word;
}

.workspace-card__edit {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--signup-text-muted);
  text-decoration: none;
  padding: 6px 10px;
  border-radius: 8px;
  transition: color 140ms ease, background 140ms ease;

  svg { opacity: 0.7; transition: opacity 140ms ease; }

  &:hover {
    color: var(--signup-brand);
    background: rgba(108, 48, 204, 0.05);
    svg { opacity: 1; }
  }
  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 2px;
  }
}

.workspace-card__roster {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  background: var(--signup-surface-subtle);
  border: 1px solid var(--signup-border-soft);
  border-radius: 10px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
}
.workspace-card__roster--solo {
  background: transparent;
  border-style: dashed;
}

.workspace-card__avatars {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.workspace-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 10.5px;
  font-weight: 800;
  color: #ffffff;
  background: #c4c4d0;
  border: 2px solid var(--signup-surface);
  box-shadow: 0 1px 2px rgba(30, 10, 60, 0.08);
  letter-spacing: 0.02em;

  & + & { margin-left: -7px; }

  &.c0 { background: #c4c4d0; color: #4a4a5a; }
  &.c1 { background: #6C30CC; }
  &.c2 { background: #5DADE2; }
  &.c3 { background: #3AC47D; }
  &.c4 { background: #FF9671; }
  &.c5 { background: #F7B924; color: #1a1a2e; }
}
.workspace-avatar--more {
  background: var(--signup-surface);
  color: var(--signup-text-soft);
  border: 1.5px dashed var(--signup-border-soft);
  font-size: 10.5px;
  font-weight: 800;
  box-shadow: none;
}
.workspace-avatar--solo {
  width: 32px;
  height: 32px;
  font-size: 11.5px;
  background: var(--signup-brand-deep);
  margin-right: 0;
}

.workspace-card__roster-line {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  color: var(--signup-text-soft);
  line-height: 1.4;

  strong {
    color: var(--signup-text);
    font-weight: 800;
  }
}

.workspace-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  margin: 0;
  border-top: 1px solid var(--signup-border-soft);
  padding-top: 10px;
  position: relative;
  z-index: 1;

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 0;
  }
}
.workspace-stat {
  position: relative;
  padding: 0 12px;

  & + & { border-left: 1px solid var(--signup-border-soft); }

  @media (max-width: 520px) {
    & + & { border-left: none; }
    &:nth-child(odd) { border-right: 1px solid var(--signup-border-soft); }
    padding: 0 10px;
  }
}
.workspace-stat dt {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--signup-text-muted);
  margin-bottom: 2px;
}
.workspace-stat dd {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: var(--signup-text);
  letter-spacing: -0.01em;
}

/* ── 2. Numbered launch timeline ─────────────────────────────── */
.launch-timeline {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  position: relative;
}

.launch-step {
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 14px;
  padding: 10px 16px 10px 0;
  position: relative;
}

/* Vertical hairline thread connecting the icon tiles. Drawn on the rail
   column so it sits between consecutive icons, not behind them. */
.launch-step__rail {
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 2px;

  /* Connector line — fades out at the very last step. */
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 30px;
    bottom: -10px;
    width: 1px;
    transform: translateX(-50%);
    background: linear-gradient(180deg,
      rgba(108, 48, 204, 0.32) 0%,
      rgba(247, 185, 36, 0.24) 100%);
  }
}
.launch-step:last-child .launch-step__rail::before { display: none; }

.launch-step__icon {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(108, 48, 204, 0.10) 0%, rgba(247, 185, 36, 0.18) 100%);
  color: var(--signup-brand);
  flex-shrink: 0;
  box-shadow: 0 0 0 3px var(--signup-surface), 0 1px 2px rgba(30, 10, 60, 0.06);
  position: relative;
  z-index: 1;

  svg { width: 16px; height: 16px; }
}
.launch-step__body {
  min-width: 0;
  padding-bottom: 2px;
}

.launch-step__index {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--signup-brand);
  opacity: 0.7;
  margin-bottom: 2px;
}
.launch-step__title {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: var(--signup-text);
  letter-spacing: -0.1px;
  margin-bottom: 3px;
}
.launch-step__desc {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  color: var(--signup-text-soft);
  line-height: 1.45;
}

/* ── 3. Disclaimer ───────────────────────────────────────────── */
.launch-disclaimer {
  text-align: center;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11.5px;
  color: var(--signup-text-muted);
  margin: 6px 0 0;
}
.launch-disclaimer a {
  color: var(--signup-brand);
  font-weight: 700;
  text-decoration: none;
}
.launch-disclaimer a:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── 4. Custom footer ─────────────────────────────────────────
   Mirrors `.signup-step__footer` in SignupStepFrame so the launch step
   gets the same sticky-bottom treatment as every other step. */
.launch-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 36px 22px;
  border-top: 1px solid var(--signup-border-soft);
  box-shadow: 0 -10px 18px -14px rgba(30, 10, 60, 0.10);
  background: var(--signup-surface);

  @media (max-width: 640px) {
    padding: 14px 22px 18px;
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 12px;
  }
}

.launch-footer__back {
  appearance: none;
  background: transparent;
  border: none;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--signup-text-muted);
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: color 140ms ease, background 140ms ease;

  &:hover:not(:disabled) {
    color: var(--signup-text);
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-color: var(--signup-text);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 2px;
  }
}

.launch-footer__cta {
  gap: 10px;

  @media (max-width: 480px) { width: 100%; }
}
.launch-footer__cta-arrow {
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.launch-footer__cta:hover:not(:disabled) .launch-footer__cta-arrow {
  transform: translateX(3px);
}

.launch-footer__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  animation: launch-spin 720ms linear infinite;
}

@keyframes launch-spin {
  to { transform: rotate(360deg); }
}
</style>
